<template>
  <template v-if="!$isMobile || noResponsive">
    <BaseTable
      class="ui-parts-table"
      v-bind="$attrs"
      size="full"
      :class="[
        isStriped && 'is-striped',
        $isMobile ? 'table-mobile' : 'table-desktop'
      ]"
    >
      <template v-if="$slots['tablecatpion']" #tablecatpion>
        {{ $slots['tablecatpion']() }}
      </template>
      <template v-if="$slots['figcaption']" #figcaption>
        {{ $slots['figcaption']() }}
      </template>
      <thead>
        <tr>
          <th v-for="(col, colIndex) in columns" scope="col" :class="colClass(col)" :style="colStyle(col)">
            <template v-if="$slots['header-' + col.key]">
              <slot :name="'header-' + col.key" :col="col" :colIndex="colIndex" :label="colLabel(col)" />
            </template>
            <template v-else-if="$slots['header']">
              <slot name="header" :col="col" :colIndex="colIndex" :label="colLabel(col)" />
            </template>
            <template v-else>
              {{ colLabel(col) }}
            </template>
          </th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="(row, rowIndex) in data" :class="rowClass(row)">
          <template v-if="row.fullline">
            <td :colspan="columns.length">
              <slot name="fullline" :row="row" :row-index="rowIndex" />
            </td>
          </template>
          <template v-else>
            <td v-for="(col, colIndex) in columns" :class="colClass(col, row)">
              <template v-if="col.key">
                <template v-if="$slots['column-' + col.key]">
                  <slot :name="'column-' + col.key"
                    :key="col.key"
                    :row="row"
                    :path="col.path"
                    :row-index="rowIndex"
                    :col-index="colIndex"
                    :def="col"
                    :values="cellValue(col, row)"
                  />
                </template>
              </template>
              <template v-else>
                {{ cellValue(col, row) }}
              </template>
            </td>
          </template>
        </tr>
      </tbody>
    </BaseTable>
  </template>
  <template v-else>
    <div class="jps-responsive-table-mobile" v-bind="$attrs">
      <template v-for="(row, rowIndex) in data" :class="rowClass(row)">
        <div v-if="row.fullline">
          <slot name="fullline" :row="row" :row-index="rowIndex" />
        </div>
        <div v-else class="jps-responsive-table-card">
          <table>
            <tbody>
              <template v-for="(col, colIndex) in columns">
                <tr v-if="!isCellNoValue(col, row)">
                  <th scope="col" :class="colClass(col)" :style="colStyle(col)">
                    <template v-if="$slots['header-' + col.key]">
                      <slot :name="'header-' + col.key" :col="col" :colIndex="colIndex" :label="colLabel(col)" />
                    </template>
                    <template v-else-if="$slots['header']">
                      <slot name="header" :col="col" :colIndex="colIndex" :label="colLabel(col)" />
                    </template>
                    <template v-else>
                      {{ colLabel(col) }}
                    </template>
                  </th>
                  <td :class="colClass(col)">
                    <template v-if="col.key">
                      <template v-if="$slots['column-' + col.key]">
                        <slot :name="'column-' + col.key"
                          :key="col.key"
                          :row="row"
                          :path="col.path"
                          :row-index="rowIndex"
                          :col-index="colIndex"
                          :def="col"
                          :values="cellValue(col, row)"
                        />
                      </template>
                    </template>
                    <template v-else>
                      {{ cellValue(col, row) }}
                    </template>
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
        </div>
      </template>
    </div>
  </template>
</template>

<script lang="ts">
export interface ColumnDef {
  path?: string[] | string
  label?: () => string
  class?: string
  firstOnly?: boolean
  /**
   * 値表示用変換関数
   */
  display?: (v: any) => string
  /**
   * slotを使う場合、keyを指定
   */
  key?: string
  sort?: string
  width?: string
}
</script>

<script lang="ts" setup>
const { $isMobile } = useNuxtApp()

interface Props {
  data: any[],
  columns: ColumnDef[],
  tableAttrs?: any,
  rowClass?: (row: any) => string,
  noResponsive?: boolean,
  isStriped?: boolean
}
const props = withDefaults(defineProps<Props>(), {
  rowClass: () => (row: any) => '',
  noResponsive: false,
})

// 行表示用
const colLabel = (col: ColumnDef) => {
  return col.label ? col.label() : ''
}
const colClass = (col: ColumnDef, row?: any) => {
  let result = []
  if (col.class) {
    result.push(col.class)
  }
  if (row && isCellNoValue(col, row)) {
    result.push('jps-no-value')
  }
  if (result.length != 0) {
    return result
  } else {
    return undefined
  }
}
const colStyle = (col: ColumnDef) => {
  return col.width ? `width: ${col.width}` : undefined
}

// 列表示用
const rowClass = (row: any) => {
  return props.rowClass ? props.rowClass(row) : undefined
}

// cell表示用
const cellValue = (col: ColumnDef, row: any) => {
  const values = createCellValue(col, row)
  if (col.key) {
    // slotを利用する場合
    return values
  } else {
    // slotを利用しない場合
    return col.firstOnly ? values?.[0] : values?.join(', ')
  }
}
const createCellValue = (col: ColumnDef, row: any) => {
  let values = []
  try {
    if (Array.isArray(col.path)) {
      col.path.forEach(p => {
        deepFind2(row, p).forEach(v => {
          values.push(col.display ? col.display(v) : v)
        })
      })
    } else if (col.path) {
      deepFind2(row, col.path).forEach(v => {
        values.push(col.display ? col.display(v) : v)
      })
    }
  } catch (e) {
    // プロパティが存在しない場合など、エラーが発生すると一覧全体が出力されなくなってしまうため、
    // 対象の項目以外は出力されるように例外をキャッチする
  }
  return values.filter(value => !isEmptyString(value))
}
const isCellNoValue = (col: ColumnDef, row: any) => {
  if (col.key) {
    return false
  } else {
    return createCellValue(col, row).length === 0
  }
}
</script>

<style lang="scss">
@use '@/assets/styles/scss/vue' as *;

.ui-parts-table {
  width: 100%;

  tbody td.jps-no-value::after {
    content: '-';
  }
}

.table-desktop {
  td {
    @include is-tablet {
      vertical-align: middle;
    }
  }


  &.is-striped {
    @include is-tablet {
      tr:hover td {
        background-color: $jps-gray-100;
      }

      tr:nth-child(odd) {
        background-color: transparent; // 奇数行の色
      }

      tr:nth-child(even) {
        background-color: $jps-gray-50; // 偶数行の色
      }

      thead {
        th {
          border-bottom: 2px solid $jps-gray-300 !important;
          font-weight: normal;
          padding: 0.5rem;
          text-align: left;
        }
      }

      td {
        border-bottom: 1px solid $jps-gray-300 !important;
      }
    }
  }
}

.hina {
  .ui-parts-table {
    thead th {
      background-color: $hina-background-green;
      border-color: $hina-accent-green !important;
      font-weight: bold;
    }
  }
}

.jps-responsive-table-mobile {
  padding: 0.5rem;

  .jps-responsive-table-card {
    overflow: hidden;
    background-color: $white;
    border: 1px solid $jps-gray-300;
    border-radius: $global-radius;
    &:not(:last-child) {
      margin-bottom: 0.75rem;
    }
    table {
      width: 100%;
      th,
      td {
        padding: 0.5rem 1rem;
        word-break: break-all;
        display: block;
        width: 100%;
        @include typo-list();
      }
      tr {
        &:nth-child(even) {
          background: $jps-gray-50;
        }
        &:hover {
          background: $jps-gray-100;
        }
      }
      th {
        font-size: 0.75rem;
        padding-bottom: 0;
        color: $jps-gray-600;
        text-align: left;
      }
      td {
        font-size: 1rem;
        padding-left: 1.75rem;
        padding-top: 0;
        border-bottom: 1px solid $jps-gray-300;
      }
      @include is-mobile {
        tr:last-child {
          td {
            // .ui-sortable-search-result-table のスタイルを上書き
            border: none !important;
          }
        }
      }
    }
  }
}
</style>
